import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Facebook, Instagram, Clock, ExternalLink, ArrowUp } from 'lucide-react';
import { companyInfo, cities } from '../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="relative bg-[#050a14] overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-blue-600/5 rounded-full blur-[128px]" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/5 rounded-full blur-[128px]" />
      </div>

      {/* CTA Section */}
      <div className="relative border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative bg-gradient-to-r from-blue-600 to-cyan-500 rounded-3xl p-8 md:p-12 overflow-hidden"
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }} />
            </div>

            <div className="relative flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  Pronto para acelerar sua conexão?
                </h3>
                <p className="text-blue-100">
                  Entre em contato e assine hoje mesmo!
                </p>
              </div>
              <motion.a
                href={`https://wa.me/${companyInfo.whatsapp}?text=Olá! Quero assinar a GFA Telecom!`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 px-8 py-4 bg-white text-blue-600 font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Assinar Agora
              </motion.a>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            <img
              src={companyInfo.logo}
              alt="GFA Telecom"
              className="h-12 w-auto mb-6 brightness-0 invert"
            />
            <p className="text-gray-500 mb-6 leading-relaxed">
              Sua conexão de fibra óptica com qualidade e velocidade.
              Atendendo as melhores cidades do Mato Grosso do Sul.
            </p>
            <div className="flex gap-3">
              <motion.a
                href={companyInfo.socialMedia.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:border-blue-500/50 hover:bg-blue-500/10 flex items-center justify-center transition-all duration-300"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Facebook className="w-5 h-5 text-gray-400" />
              </motion.a>
              <motion.a
                href={companyInfo.socialMedia.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:border-pink-500/50 hover:bg-pink-500/10 flex items-center justify-center transition-all duration-300"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <Instagram className="w-5 h-5 text-gray-400" />
              </motion.a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-6">Links Rápidos</h3>
            <ul className="space-y-3">
              {[
                { label: 'Nossos Planos', href: '#planos' },
                { label: 'Vantagens', href: '#vantagens' },
                { label: 'Teste de Velocidade', href: '#speedtest' },
                { label: 'FAQ', href: '#faq' },
                { label: 'Central do Assinante', href: 'https://gfanet.com.br/cliente', external: true },
              ].map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    target={link.external ? '_blank' : undefined}
                    rel={link.external ? 'noopener noreferrer' : undefined}
                    className="text-gray-500 hover:text-cyan-400 transition-colors flex items-center gap-2"
                  >
                    {link.label}
                    {link.external && <ExternalLink className="w-3 h-3" />}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Cities */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-6">Cidades Atendidas</h3>
            <ul className="space-y-3">
              {cities.map((city) => (
                <li key={city.id} className="flex items-center gap-2 text-gray-500">
                  <MapPin className="w-4 h-4 text-cyan-500" />
                  {city.name}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-lg font-semibold text-white mb-6">Contato</h3>
            <ul className="space-y-4">
              <li>
                <a
                  href={`tel:${companyInfo.phone.replace(/\D/g, '')}`}
                  className="flex items-center gap-4 text-gray-500 hover:text-white transition-colors"
                >
                  <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-cyan-500" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Telefone</p>
                    <p className="font-medium">{companyInfo.phone}</p>
                  </div>
                </a>
              </li>
              <li>
                <a
                  href={`mailto:${companyInfo.email}`}
                  className="flex items-center gap-4 text-gray-500 hover:text-white transition-colors"
                >
                  <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-cyan-500" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">E-mail</p>
                    <p className="font-medium">{companyInfo.email}</p>
                  </div>
                </a>
              </li>
              <li className="flex items-start gap-4 text-gray-500">
                <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-cyan-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-600">Horário de Atendimento</p>
                  <p className="font-medium text-sm">Seg a Sex: 07:00 às 11:00</p>
                  <p className="font-medium text-sm">13:00 às 17:30</p>
                  <p className="font-medium text-sm">Sáb: 07:30 às 11:00</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="relative border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-600 text-sm text-center md:text-left">
              © {currentYear} {companyInfo.name}. Todos os direitos reservados.
            </p>
            <div className="flex items-center gap-6">
              <div className="flex gap-6 text-sm">
                <a href="#" className="text-gray-600 hover:text-white transition-colors">
                  Termos de Uso
                </a>
                <a href="#" className="text-gray-600 hover:text-white transition-colors">
                  Privacidade
                </a>
              </div>
              {/* Back to Top */}
              <motion.button
                onClick={scrollToTop}
                className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 hover:border-cyan-500/50 hover:bg-cyan-500/10 flex items-center justify-center transition-all duration-300"
                whileHover={{ scale: 1.1, y: -2 }}
                whileTap={{ scale: 0.9 }}
              >
                <ArrowUp className="w-5 h-5 text-gray-400" />
              </motion.button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
